/**
 * One reading of the database, turned into the shapes the engine takes.
 *
 * These adapters were written for `supabase/functions/_shared/engine.ts` in
 * `025_order_endpoint`, where the server turns `public.submission_context()`
 * into an `OrderInput` and an `EngineParams`. `026_customer_page` needs the
 * same conversion in the browser, against `public.menu_context()`, and §19.2
 * principle 2 only means anything if the page and the server read one reading
 * the same way. So the adapters live here, in the package both of them
 * already depend on, and `_shared/engine.ts` re-exports them unchanged.
 *
 * **Nothing here holds a figure.** Every multiplier, band, margin rate, zone
 * fee, price, production unit and ceiling is read out of the context, which
 * read it out of `public.params`, `public.occasions`,
 * `public.delivery_zones` and the publication. The only literals are the
 * register key names and the four category keys of `public.price_category`.
 */

import { dishUnit } from './index.ts'
import { add, fromDecimal, multiply, rational, ZERO, type Rational } from './rational.ts'
import type {
  ConferenceSelection,
  DeliveryZone,
  Dish,
  EngineParams,
  GlutenFreeAllocation,
  MoneyParams,
  Occasion,
  QuantityParams,
  Side,
} from './types.ts'

/** The register as the context sends it: key to the text value §12 holds. */
export type Params = Record<string, string | null>

/** One row of the `dishes` array in the context. */
export interface PublishedDish {
  id: string
  name_hu: string | null
  name_en: string | null
  category: string | null
  menu_group: string | null
  price_category: string | null
  is_drink: boolean
  production_unit: number | null
  min_production: number | null
  lead_bd: number | null
  effective_lead_bd: number | null
  net_price: number | null
  featured: boolean | null
  cafe: boolean | null
  gluten_free_eligible: boolean | null
  lead_time_ok: boolean
}

export interface OccasionRow {
  key: string
  sav_point: number | string | null
  sw_point: number | string | null
  sav_dishes_min: number | null
  sav_dishes_max: number | null
  sw_dishes_min: number | null
  sw_dishes_max: number | null
}

export interface ZoneRow {
  zone: string
  fee_gross: number | null
  free_above_gross: number | null
  min_order_gross: number | null
  fee_quoted: boolean
  review: boolean
}

export class MissingParameterError extends Error {
  constructor(key: string) {
    super(`the register has no ${key}; §12 holds it and the engine cannot run without it`)
    this.name = 'MissingParameterError'
  }
}

function text(params: Params, key: string): string {
  const value = params[key]
  if (value === undefined || value === null || String(value).trim() === '') {
    throw new MissingParameterError(key)
  }
  return String(value)
}

const exact = (params: Params, key: string) => fromDecimal(text(params, key))
const whole = (params: Params, key: string): number => {
  const parsed = Number(text(params, key).replace(',', '.'))
  if (!Number.isFinite(parsed)) throw new MissingParameterError(key)
  return parsed
}

/**
 * §6.1–§6.7 and `build/FIXES.md` item 2. The serving-window and profile keys
 * are the enum values of `public.serving_window` and `public.guest_profile`,
 * so `orders.serving_window` and `orders.guest_profile` index this record
 * directly and no second spelling of either exists on the server.
 */
export function quantityParamsFrom(params: Params): QuantityParams {
  return {
    servingWindowMultipliers: {
      '2h': exact(params, 'duration_mult_2h'),
      '3h': exact(params, 'duration_mult_3h'),
      '4h': exact(params, 'duration_mult_4h'),
      '5h_plus': exact(params, 'duration_mult_5h_plus'),
    },
    profileMultipliers: {
      light: exact(params, 'profile_mult_light'),
      average: exact(params, 'profile_mult_average'),
      hearty: exact(params, 'profile_mult_hearty'),
    },
    buffetSelfServiceMultiplier: exact(params, 'buffet_self_service_mult'),
    balanceShift: exact(params, 'balance_shift'),
    balanceFloor: exact(params, 'balance_floor'),
    marginBands: [whole(params, 'margin_band_small_max'), whole(params, 'margin_band_mid_max')],
    marginStandardPct: [
      exact(params, 'margin_pct_small_std'),
      exact(params, 'margin_pct_mid_std'),
      exact(params, 'margin_pct_large_std'),
    ],
    marginFeaturedPct: [
      exact(params, 'margin_pct_small_featured'),
      exact(params, 'margin_pct_mid_featured'),
      exact(params, 'margin_pct_large_featured'),
    ],
    dishBands: [whole(params, 'dish_band_small_max'), whole(params, 'dish_band_mid_max')],
    dishLimitSavoury: [
      whole(params, 'dish_limit_sav_small'),
      whole(params, 'dish_limit_sav_mid'),
      whole(params, 'dish_limit_sav_large'),
    ],
    dishLimitSweet: [
      whole(params, 'dish_limit_sw_small'),
      whole(params, 'dish_limit_sw_mid'),
      whole(params, 'dish_limit_sw_large'),
    ],
    kitchenCeilingSavoury: whole(params, 'max_sav_varieties'),
    kitchenCeilingSweet: whole(params, 'max_sw_varieties'),
    fruitPerGuest: exact(params, 'fruit_per_guest'),
    maxFruitVarieties: whole(params, 'max_fruit_varieties'),
    glutenFreeAllocation: glutenFreeAllocationFrom(params),
    glutenFreeDishesPerSide: [
      whole(params, 'gf_dishes_per_side_small'),
      whole(params, 'gf_dishes_per_side_large'),
    ],
    glutenFreeLargeFrom: whole(params, 'gf_dishes_large_from'),
  }
}

/**
 * §12 `gf_allocation`, read by name. A value the engine does not know fails
 * by name rather than falling back to either mode: the two give different
 * quantities, and a guess between them would be a quantity nobody decided.
 */
export function glutenFreeAllocationFrom(params: Params): GlutenFreeAllocation {
  const value = text(params, 'gf_allocation').trim()
  if (value === 'gf_box_per_side' || value === 'fewest_dishes_keep_total') return value
  throw new MissingParameterError('gf_allocation')
}

export function zonesFrom(rows: Record<string, ZoneRow>): Record<string, DeliveryZone> {
  return Object.fromEntries(
    Object.values(rows).map((row) => [
      row.zone,
      {
        zone: row.zone,
        feeGross: row.fee_gross,
        freeAboveGross: row.free_above_gross,
        minOrderGross: row.min_order_gross ?? 0,
        feeQuoted: row.fee_quoted === true,
        review: row.review === true,
      } satisfies DeliveryZone,
    ]),
  )
}

export function moneyParamsFrom(params: Params, zoneRows: Record<string, ZoneRow>): MoneyParams {
  return {
    vatPct: exact(params, 'vat_rate'),
    minPickupGross: whole(params, 'min_pickup'),
    zones: zonesFrom(zoneRows),
  }
}

export function engineParamsFrom(params: Params, zoneRows: Record<string, ZoneRow>): EngineParams {
  return { quantity: quantityParamsFrom(params), money: moneyParamsFrom(params, zoneRows) }
}

/**
 * §6.2: the two standing receptions take a duration. The occasion row says
 * so in prose (`notes` and `reference_window`), so the key is what decides
 * it, the same way `packages/engine/test/fixtures/index.ts` reads it.
 *
 * §5.3's *leading dishes* are not set here. They are a pre-selection rank,
 * and the rank is the page's: `026_customer_page` passes them in when it
 * builds the occasion it pre-selects from, and by the time an order is
 * submitted the customer has a basket and the rank cannot change a quantity.
 */
export const EVENING_RECEPTION = 'fog_est'

/** §6.1's daytime figure set — *Nappali állófogadás, networking*. */
export const DAYTIME_RECEPTION = 'fog_nap'

/**
 * **Run 18 item R18-3 (PROPOSED 20261016, Barna): *Egyéb* / *Other*.** The
 * last occasion on the page, for an event none of the other four describes.
 *
 *   * its figures are `other_sav` and `other_sw` in the register, not on the
 *     occasion row, *so they can change without code*;
 *   * **no duration is asked and no multiplier applies** — `referenceWindow`
 *     is null, the same as the working lunch and the coffee break;
 *   * the appetite question and its multiplier apply as everywhere else;
 *   * the meal question of run 17 item B11 is not shown: it chooses between
 *     the two reception figure sets and this is neither;
 *   * the live check of item B4 does not appear, in any state — the figures
 *     are a fixed default and not a recommendation, which is also why R18-3
 *     keeps these orders out of the calibration cells (§11.10).
 */
export const OTHER_OCCASION = 'other'

/**
 * **Run 19 §6 (DECIDED 20261016, Barna): *Könnyű vacsora* / *Light
 * dinner*.** A shortcut to *Állófogadás* answered *Igen*.
 *
 * It is a real occasion row with the `fog_est` figures on it — 8,5 savoury
 * and 3,5 sweet pieces per guest — so the duration and appetite multipliers,
 * the §6.4 margin and §6.7's rounding all apply exactly as they do there.
 * What it does **not** have is the meal question: the customer who picks it
 * has already answered it.
 *
 * Its reference serving window is the evening set's own, `2h`, for the same
 * reason `fog_est`'s is: the figures are that set's and are measured at that
 * band.
 */
export const LIGHT_DINNER = 'konnyu_vacsora'

/** §12: where *Egyéb*'s two figures live. PROPOSED — 8,5 and 3,0. */
export const OTHER_POINT_KEYS: Readonly<Record<Side, string>> = Object.freeze({
  sav: 'other_sav',
  sw: 'other_sw',
})

/**
 * **Run 18 item R18-1.** The evening figure set's own reference serving
 * window. `public.occasions.reference_window` carries it in prose for this
 * row — *About 2 h, after 16–17h* — and `duration_mult_2h` is 1,0, so
 * reading the multiplier as a ratio to this band leaves every `fog_est`
 * quantity exactly where it was. It is a constant rather than a register row
 * because nobody has asked for it to move; `fog_nap`'s is a register row
 * because R18-1 says so and because the figure behind it is an assumption.
 */
export const EVENING_REFERENCE_WINDOW = '2h'

/**
 * **Run 18 item R18-1.** §12: the reference serving window the *daytime*
 * figure set — 3,5 savoury and 2,5 sweet pieces per guest — is measured at.
 * The duration multiplier then applies to `fog_nap` as a ratio to this band,
 * exactly as it does to `fog_est` against its own.
 *
 * The value is an **assumption** (A14), not a figure anybody has decided:
 * the open question for Balázs is *what serving window do the daytime
 * figures assume?*. Held in the register so his answer is a value, not a
 * code change.
 */
export const DAYTIME_REFERENCE_WINDOW_PARAM = 'fog_nap_ref_band'

/**
 * The reference serving window of one occasion row, or `null` where the
 * occasion takes no duration at all (§6.2 and F2: the coffee break, the
 * working lunch and the conference).
 *
 * `fog_nap`'s comes from the register and so needs the reading's parameters.
 * A caller that hands over a daytime reception without them has not been
 * told about R18-1, so it fails by name — the same way the conference fails
 * by name without the rows it is built from — rather than quietly pricing
 * the day as if the duration did not matter.
 */
export function referenceWindowOf(
  row: OccasionRow,
  params: Params | undefined,
): string | null {
  /* Run 19 §6: *Könnyű vacsora* carries the evening set's figures, so it
     carries the evening set's reference band with them. Reading it off the
     same constant is what keeps the two identical to the piece. */
  if (row.key === EVENING_RECEPTION || row.key === LIGHT_DINNER) return EVENING_REFERENCE_WINDOW
  if (row.key !== DAYTIME_RECEPTION) return null
  if (params === undefined) throw new MissingParameterError(DAYTIME_REFERENCE_WINDOW_PARAM)
  return text(params, DAYTIME_REFERENCE_WINDOW_PARAM).trim()
}

/* ------------------------------------------------------------------ *
 * The all-day conference (build/FIXES.md item 73, F2)
 * ------------------------------------------------------------------ */

/**
 * Barna's ruling 2 of 8 October 2026 replaced `konf_eb` and `konf_ff` with
 * one occasion *made of* the others: an optional welcome, one to three
 * coffee breaks, and an optional lunch. Its row in `public.occasions` holds
 * **no piece figures**, and that is the ruling rather than a gap — F2:
 * *Read the figures from the occasion table at run time, never copy them, so
 * a change to Kávészünet or Munkaebéd flows through.*
 */
export const CONFERENCE = 'konf'

/** Welcome and every break are priced as a *Kávészünet*. */
export const CONFERENCE_BREAK_OCCASION = 'coffee'

/**
 * Lunch is priced as a *Munkaebéd*. F2: *Lunch inside a conference is
 * buffet-style, never individual boxes, even though Munkaebéd on its own is
 * boxed.* That is a packing instruction and not a quantity: §6.5's
 * `buffet_self_service_mult` is 1,0 and `basePiecesPerGuest` already applies
 * it to every occasion, so there is no second figure here for the engine to
 * choose between. The occasion row carries the sentence in its `notes`.
 */
export const CONFERENCE_LUNCH_OCCASION = 'munka'

/** F2: one to three coffee breaks. */
export const CONFERENCE_MIN_BREAKS = 1
export const CONFERENCE_MAX_BREAKS = 3

/**
 * F2's defaults — welcome yes, two breaks, lunch no — are PROPOSED, and are
 * logged in `DECISIONS_LOG.md` under *Defaults chosen in run7, for Barna*.
 * They live here, once, so the page and the server open on the same
 * conference and a later calibration moves both.
 */
export const CONFERENCE_DEFAULTS: ConferenceSelection = Object.freeze({
  welcome: true,
  breaks: 2,
  lunch: false,
})

/** An occasion the reading did not carry. Named, like `MissingParameterError`. */
export class MissingOccasionError extends Error {
  constructor(key: string) {
    super(`the reading has no occasion ${key}; §6.1 holds it and the conference is built out of it`)
    this.name = 'MissingOccasionError'
  }
}

/**
 * Whatever the page or an order payload says, read as the three answers.
 * Anything missing or out of range falls back to the PROPOSED default rather
 * than to zero: an order with no `breaks` is an order that was never asked
 * the question, and a conference with no element at all would price a whole
 * day at nothing.
 */
export function conferenceSelectionFrom(
  raw: Partial<ConferenceSelection> | null | undefined,
): ConferenceSelection {
  const breaks = Number(raw?.breaks)
  return {
    welcome: typeof raw?.welcome === 'boolean' ? raw.welcome : CONFERENCE_DEFAULTS.welcome,
    breaks: Number.isInteger(breaks)
      ? Math.min(Math.max(breaks, CONFERENCE_MIN_BREAKS), CONFERENCE_MAX_BREAKS)
      : CONFERENCE_DEFAULTS.breaks,
    lunch: typeof raw?.lunch === 'boolean' ? raw.lunch : CONFERENCE_DEFAULTS.lunch,
  }
}

/**
 * How many of each element the day holds. F3's refreshment drinks and F7's
 * coffee both count occasions rather than pieces, so the count is named here
 * once and `081` reads it instead of writing the same sum a second time.
 */
export function conferenceElements(selection: ConferenceSelection): {
  breakElements: number
  lunchElements: number
  total: number
} {
  const breakElements = (selection.welcome ? 1 : 0) + selection.breaks
  const lunchElements = selection.lunch ? 1 : 0
  return { breakElements, lunchElements, total: breakElements + lunchElements }
}

/**
 * Run 9, item 3 (`docs/PLT_catering_decisions_run9_20261010.md`): the
 * conference has its own per-element figures in the register, PROPOSED
 * 20261010 for Balázs to confirm, and reads them **instead of** the
 * Kávészünet and Munkaebéd rows. Every break repeated Kávészünet's
 * sweet-heavy split and the lunch added Munkaebéd's 2,5 sweet on top, which
 * is what tipped a full day over the day's capacity (last night's item 10).
 * Standalone Kávészünet and Munkaebéd are unchanged.
 */
export const CONFERENCE_ELEMENT_KEYS = {
  welcome: { sav: 'konf_welcome_sav', sw: 'konf_welcome_sw' },
  break: { sav: 'konf_break_sav', sw: 'konf_break_sw' },
  lunch: { sav: 'konf_lunch_sav', sw: 'konf_lunch_sw' },
} as const

/**
 * F2's formula, to the piece, with run 9's figures:
 *
 *   savoury = (welcome ? konf_welcome_sav : 0) + breaks × konf_break_sav + (lunch ? konf_lunch_sav : 0)
 *   sweet   = (welcome ? konf_welcome_sw  : 0) + breaks × konf_break_sw  + (lunch ? konf_lunch_sw  : 0)
 *
 * before the profile, serving-window and buffet multipliers, which
 * `basePiecesPerGuest` applies as it does for every other occasion. The
 * figures are read out of the register at run time and never copied: change
 * a `konf_*` row and the conference moves with it. A missing row fails by
 * name (`MissingParameterError`) rather than pricing an element at nothing.
 *
 * `occasions` is still taken, so no caller changes: F2 built the day out of
 * the other rows, and the signature stays the one every caller already uses.
 *
 * The dish range (§6.6) is the `konf` row's own without lunch, and the
 * register's `konf_lunch_dishes_*` with it — the former `konf_ff` range,
 * carried on 2026-10-07.
 */
export function conferenceOccasionFrom(
  row: OccasionRow,
  _occasions: Readonly<Record<string, OccasionRow>>,
  params: Params,
  selection: Partial<ConferenceSelection> | null | undefined,
): Occasion {
  const chosen = conferenceSelectionFrom(selection)
  const perHead = (side: Side): Rational => {
    let sum: Rational = ZERO
    if (chosen.welcome) sum = add(sum, exact(params, CONFERENCE_ELEMENT_KEYS.welcome[side]))
    sum = add(sum, multiply(exact(params, CONFERENCE_ELEMENT_KEYS.break[side]), rational(chosen.breaks)))
    if (chosen.lunch) sum = add(sum, exact(params, CONFERENCE_ELEMENT_KEYS.lunch[side]))
    return sum
  }

  const dishRange = (side: Side): readonly [number, number] => {
    if (chosen.lunch) {
      return [whole(params, `konf_lunch_dishes_${side}_min`), whole(params, `konf_lunch_dishes_${side}_max`)]
    }
    return side === 'sav'
      ? [row.sav_dishes_min ?? 0, row.sav_dishes_max ?? 0]
      : [row.sw_dishes_min ?? 0, row.sw_dishes_max ?? 0]
  }

  return {
    key: row.key,
    savouryPoint: perHead('sav'),
    sweetPoint: perHead('sw'),
    /* F2: *Duration is not asked for the conference occasion.* Run 18 item
       R18-1 leaves that untouched: *Egész napos konferencia is unchanged.* */
    referenceWindow: null,
    servingWindowApplies: false,
    savouryDishRange: dishRange('sav'),
    sweetDishRange: dishRange('sw'),
  }
}

/**
 * One occasion row as the engine takes it.
 *
 * `context` is what the conference needs: the other rows of the same
 * reading, the register, and the three answers. A `konf` row handed over
 * without it is a caller that has not been told about F2, so it fails by
 * name rather than pricing a whole day at nothing.
 *
 * Since run 18 item R18-1 a `fog_nap` row needs `params` too, for its
 * reference serving window, and fails by name the same way.
 */
export function occasionFrom(
  row: OccasionRow,
  context?: {
    occasions?: Readonly<Record<string, OccasionRow>>
    params?: Params
    conference?: Partial<ConferenceSelection> | null
  },
): Occasion {
  if (row.key === CONFERENCE) {
    const occasions = context?.occasions
    const params = context?.params
    if (occasions === undefined) throw new MissingOccasionError(CONFERENCE_BREAK_OCCASION)
    if (params === undefined) throw new MissingParameterError('konf_lunch_dishes_sav_min')
    return conferenceOccasionFrom(row, occasions, params, context?.conference)
  }
  const referenceWindow = referenceWindowOf(row, context?.params)
  return {
    key: row.key,
    savouryPoint: pointOf(row, 'sav', context?.params),
    sweetPoint: pointOf(row, 'sw', context?.params),
    referenceWindow,
    servingWindowApplies: referenceWindow !== null,
    savouryDishRange: [row.sav_dishes_min ?? 0, row.sav_dishes_max ?? 0],
    sweetDishRange: [row.sw_dishes_min ?? 0, row.sw_dishes_max ?? 0],
  }
}

/**
 * §6.1's point for one side. Every occasion but one carries its own on its
 * row; **run 18 item R18-3's *Egyéb* carries it in the register instead**,
 * because R18-3 asks for figures that *can change without code* and the row
 * is generated from the parameter workbook, which does not know this
 * occasion. The row's own point columns are null for it, the same way the
 * conference's are, and that is the ruling rather than a gap.
 */
function pointOf(row: OccasionRow, side: Side, params: Params | undefined): Rational {
  if (row.key !== OTHER_OCCASION) return fromDecimal(String((side === 'sav' ? row.sav_point : row.sw_point) ?? 0))
  if (params === undefined) throw new MissingParameterError(OTHER_POINT_KEYS[side])
  return exact(params, OTHER_POINT_KEYS[side])
}

/** §5.11 and `public.price_category`: which side of the menu a category is. */
export function sideOfCategory(priceCategory: string | null): Side | null {
  if (priceCategory === 'sos') return 'sav'
  if (priceCategory === 'edes') return 'sw'
  return null
}

/**
 * A published dish as the engine takes it.
 *
 * `keptByDecision` is false on every row and that is not an oversight: §7.3
 * records the decision in a note on the catalogue row rather than in a
 * column, and it is the fourth rank of the §5.3 *pre-selection* order.
 * Pre-selection is the page's; the server is given a basket. It changes no
 * quantity here.
 */
export function dishFrom(row: PublishedDish): Dish | null {
  const side = sideOfCategory(row.price_category)
  if (side === null) return null
  if (row.net_price === null) return null
  return {
    id: row.id,
    side,
    productionUnit: row.production_unit ?? 1,
    minProduction: row.min_production,
    netPrice: row.net_price,
    featured: row.featured === true,
    glutenFreeEligible: row.gluten_free_eligible === true,
    cafe: row.cafe === true,
    keptByDecision: false,
    unit: dishUnit({ category: row.category, nameHu: row.name_hu, nameEn: row.name_en }),
  }
}
